import React from 'react';

import { TextInput, FormItem } from '@components/admin/Forms';
import { RadioGroupInput } from '@components/Form';
import { CheckboxInput } from '@components/Forms/CheckboxInput';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { instructionStrings } from '@data/instruction';
import { CustomsInstructionSpecialNoteItem } from '@models/CustomsInstruction';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useField } from 'formik';

const useStyles = makeMuiStyles((theme, common) => ({
  bookingInquirySpecialNoteContainer: {
    ...common.textGray700,
    ...common.flexColumn,
    rowGap: '0.5rem',
    padding: '1rem',
  },
  bookingInquiryCustomsSpecialNoteContainer: {
    ...common.flexColumn,
    paddingTop: '0.5rem',
    marginLeft: '0.1rem',
    rowGap: '0.25rem',
  },
  specialNoteTitle: {
    ...common.textGray600,
    marginBottom: '0.25rem',
  },
  specialNoteLabel: {
    width: 150,
  },
}));

export const BookingInquirySpecialNote: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [customsInstructionHasSpecialNoteField] = useField<boolean>(
    'customs_instruction.has_special_note',
  );
  const [customsInstructionSpecialNoteField] = useField<CustomsInstructionSpecialNoteItem[]>(
    'customs_instruction.special_note',
  );
  const [customsInstructionSpecialNoteTitlesField] = useField<string[]>(
    'customs_instruction.special_note_titles',
  );

  const [deliveryInstructionHasSpecialNoteField] = useField<boolean>(
    'delivery_instruction.has_special_note',
  );
  const [hasSpecialNoteField] = useField<boolean>('has_special_note');

  return (
    <div className={classes.bookingInquirySpecialNoteContainer}>
      <FormItem
        name="customs_instruction.special_note_titles"
        label={
          <>
            {instructionStrings.customsInstructionSpecialNote}
            <span className={classes.textError}>*</span>
          </>
        }
        classes={{
          label: classes.specialNoteLabel,
        }}
      >
        <div style={{ flex: 1 }}>
          <RadioGroupInput
            noLabel
            label=""
            name="customs_instruction.has_special_note"
            iconSize="small"
            options={[
              {
                value: false,
                label: instructionStrings.customsInstructionHasSpecialNoteFalse,
              },
              {
                value: true,
                label: instructionStrings.customsInstructionHasSpecialNoteTrue,
              },
            ]}
            formControlLabelClasses={{
              label: classes.middleTextImportant,
            }}
          />

          {customsInstructionHasSpecialNoteField.value && (
            <div className={classes.bookingInquiryCustomsSpecialNoteContainer}>
              {customsInstructionSpecialNoteField.value.map((item, i) => (
                <div key={item.title}>
                  <CheckboxInput
                    name="customs_instruction.special_note_titles"
                    value={item.title}
                    label={item.title}
                    classes={{ container: classes.specialNoteTitle }}
                  />
                  {customsInstructionSpecialNoteTitlesField.value.includes(item.title) && (
                    <TextInput
                      name={`customs_instruction.special_note.${i}.note`}
                      noLabel
                      placeholder={instructionStrings.customsInstructionSpecialNotePlaceholder(
                        item.title,
                      )}
                      multiline
                      rows={2}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </FormItem>

      <FormItem
        name="delivery_instruction.special_note"
        label={
          <>
            {instructionStrings.deliveryInstructionSpecialNote}
            <span className={classes.textError}>*</span>
          </>
        }
        classes={{
          label: classes.specialNoteLabel,
        }}
      >
        <div style={{ flex: 1 }}>
          <RadioGroupInput
            noLabel
            label=""
            name="delivery_instruction.has_special_note"
            iconSize="small"
            options={[
              {
                value: false,
                label: instructionStrings.deliveryInstructionHasSpecialNoteFalse,
              },
              {
                value: true,
                label: instructionStrings.deliveryInstructionHasSpecialNoteTrue,
              },
            ]}
            formControlLabelClasses={{
              label: classes.middleTextImportant,
            }}
          />

          {deliveryInstructionHasSpecialNoteField.value && (
            <TextInput
              name="delivery_instruction.special_note"
              noLabel
              placeholder={instructionStrings.deliveryInstructionSpecialNotePlaceholder}
              multiline
              noErrorMessage
              rows={2}
            />
          )}
        </div>
      </FormItem>

      <FormItem
        name="special_note"
        label={
          <>
            {bookingInquiryStrings.selfSpecialNote} <span className={classes.textError}>*</span>
          </>
        }
        classes={{
          label: classes.specialNoteLabel,
        }}
      >
        <div style={{ flex: 1 }}>
          <RadioGroupInput
            noLabel
            label=""
            name="has_special_note"
            iconSize="small"
            options={[
              {
                value: false,
                label: bookingInquiryStrings.hasSpecialNoteFalse,
              },
              {
                value: true,
                label: bookingInquiryStrings.hasSpecialNoteTrue,
              },
            ]}
            formControlLabelClasses={{
              label: classes.middleTextImportant,
            }}
          />

          {hasSpecialNoteField.value && (
            <TextInput
              name="special_note"
              noLabel
              placeholder={bookingInquiryStrings.specialNotePlaceholder}
              multiline
              noErrorMessage
              rows={2}
            />
          )}
        </div>
      </FormItem>
    </div>
  );
};
